/**
 * TeamPanel — Floating panel for org member management (Phase 4).
 *
 * Sections: invite form (owner/admin only), members table with inline role
 * change + remove, pending invitations (read-only).
 *
 * Permission model mirrors the backend:
 *   - viewer/operator → read-only list
 *   - owner/admin     → invite + role-change + remove
 *
 * The backend prevents demoting/removing the last owner; we duplicate that
 * client-side for clearer UX (disabled controls instead of an error).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../../services/apiFetch';
import { useAuthStore } from '../../stores/authStore';
import './TeamPanel.css';

type OrgRole = 'owner' | 'admin' | 'operator' | 'viewer';
type InviteRole = Exclude<OrgRole, 'owner'>;

interface Member {
  id: string;
  org_id: string;
  user_id: string;
  email: string;
  display_name?: string | null;
  role: OrgRole;
  status: string;
  joined_at: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: InviteRole;
  status: string;
  expires_at: string;
  invited_by: string;
  invited_by_name?: string | null;
}

interface TeamPanelProps {
  onClose: () => void;
}

const ROLE_OPTIONS: InviteRole[] = ['admin', 'operator', 'viewer'];

export default function TeamPanel({ onClose }: TeamPanelProps) {
  const currentOrg = useAuthStore((s) => s.currentOrg);
  const currentUserId = useAuthStore((s) => s.user?.id);

  const canManage = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const isOwner = currentOrg?.role === 'owner';

  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('viewer');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [rowPending, setRowPending] = useState<Record<string, boolean>>({});

  const ownerCount = useMemo(
    () => members.filter((m) => m.role === 'owner' && m.status === 'active').length,
    [members],
  );

  const refresh = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    setError(null);
    try {
      const [memberList, inviteList] = await Promise.all([
        apiFetch<Member[]>(`/api/v2/organizations/${currentOrg.id}/members`),
        apiFetch<PendingInvite[]>(`/api/v2/organizations/${currentOrg.id}/invitations`).catch(
          () => [] as PendingInvite[],
        ),
      ]);
      setMembers(memberList);
      setInvites(inviteList);
    } catch (err) {
      setError(messageFromError(err, 'Failed to load team'));
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email.includes('@')) {
      setError('Enter a valid email address');
      return;
    }
    setInviteSubmitting(true);
    setError(null);
    setInviteSuccess(null);
    try {
      await apiFetch(`/api/v2/organizations/${currentOrg.id}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email, role: inviteRole }),
      });
      setInviteSuccess(`Invitation sent to ${email}`);
      setInviteEmail('');
      setInviteRole('viewer');
      await refresh();
    } catch (err) {
      setError(messageFromError(err, 'Failed to send invitation'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleRoleChange = async (member: Member, nextRole: OrgRole) => {
    if (!currentOrg) return;
    if (member.role === nextRole) return;
    if (member.role === 'owner' && nextRole !== 'owner' && ownerCount <= 1) {
      setError('Cannot demote the last owner');
      return;
    }
    setRowPending((p) => ({ ...p, [member.user_id]: true }));
    setError(null);
    try {
      await apiFetch(`/api/v2/organizations/${currentOrg.id}/members/${member.user_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: nextRole }),
      });
      await refresh();
    } catch (err) {
      setError(messageFromError(err, 'Failed to update role'));
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[member.user_id];
        return next;
      });
    }
  };

  const handleRemove = async (member: Member) => {
    if (!currentOrg) return;
    if (member.role === 'owner' && ownerCount <= 1) {
      setError('Cannot remove the last owner');
      return;
    }
    const confirmed = window.confirm(
      `Remove ${member.display_name || member.email} from ${currentOrg.name}?`,
    );
    if (!confirmed) return;
    setRowPending((p) => ({ ...p, [member.user_id]: true }));
    setError(null);
    try {
      await apiFetch(`/api/v2/organizations/${currentOrg.id}/members/${member.user_id}`, {
        method: 'DELETE',
      });
      await refresh();
    } catch (err) {
      setError(messageFromError(err, 'Failed to remove member'));
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[member.user_id];
        return next;
      });
    }
  };

  return (
    <div className="team-panel">
      <header className="team-panel__header">
        <span className="team-panel__title">
          Team{currentOrg && <span className="team-panel__org">· {currentOrg.name}</span>}
        </span>
        <button
          type="button"
          className="team-panel__header-btn"
          onClick={onClose}
          aria-label="Close team panel"
        >
          ×
        </button>
      </header>

      <div className="team-panel__body">
        {!currentOrg && (
          <div className="team-panel__placeholder">
            You're not currently a member of any organization.
          </div>
        )}

        {error && <div className="team-panel__alert team-panel__alert--error">{error}</div>}
        {inviteSuccess && (
          <div className="team-panel__alert team-panel__alert--success">{inviteSuccess}</div>
        )}

        {currentOrg && canManage && (
          <section className="team-panel__section">
            <h3 className="team-panel__section-title">Invite a teammate</h3>
            <form onSubmit={handleInvite} className="team-panel__invite-form">
              <input
                type="email"
                className="team-panel__input"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
              <div className="team-panel__invite-row">
                <select
                  className="team-panel__input team-panel__input--role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as InviteRole)}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>{capitalize(r)}</option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="team-panel__primary"
                  disabled={inviteSubmitting}
                >
                  {inviteSubmitting ? 'Sending…' : 'Send invite'}
                </button>
              </div>
            </form>
          </section>
        )}

        {currentOrg && (
          <section className="team-panel__section">
            <h3 className="team-panel__section-title">
              Members{!loading && <span className="team-panel__count">{members.length}</span>}
            </h3>
            {loading ? (
              <div className="team-panel__placeholder">Loading…</div>
            ) : members.length === 0 ? (
              <div className="team-panel__placeholder">No members yet.</div>
            ) : (
              <ul className="team-panel__list">
                {members.map((m) => {
                  const isSelf = m.user_id === currentUserId;
                  const isLastOwner = m.role === 'owner' && ownerCount <= 1;
                  const pending = !!rowPending[m.user_id];
                  const canEditRole =
                    canManage && !pending && (isOwner || m.role !== 'owner');
                  const canRemove =
                    canManage && !pending && !isSelf && !isLastOwner && (isOwner || m.role !== 'owner');
                  return (
                    <li key={m.user_id} className="team-panel__row">
                      <div className="team-panel__row-main">
                        <div className="team-panel__user-name">
                          {m.display_name || m.email}
                          {isSelf && <span className="team-panel__chip">you</span>}
                        </div>
                        {m.display_name && (
                          <div className="team-panel__user-email">{m.email}</div>
                        )}
                      </div>
                      <div className="team-panel__row-side">
                        {canEditRole ? (
                          <select
                            className="team-panel__input team-panel__input--inline"
                            value={m.role}
                            disabled={pending || isLastOwner}
                            onChange={(e) => handleRoleChange(m, e.target.value as OrgRole)}
                          >
                            {isOwner && <option value="owner">Owner</option>}
                            <option value="admin">Admin</option>
                            <option value="operator">Operator</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        ) : (
                          <span className={`team-panel__role team-panel__role--${m.role}`}>
                            {capitalize(m.role)}
                          </span>
                        )}
                        {canRemove && (
                          <button
                            type="button"
                            className="team-panel__danger-btn"
                            onClick={() => handleRemove(m)}
                            disabled={pending}
                            title="Remove from organization"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {currentOrg && invites.length > 0 && (
          <section className="team-panel__section">
            <h3 className="team-panel__section-title">
              Pending invitations
              <span className="team-panel__count">{invites.length}</span>
            </h3>
            <ul className="team-panel__list">
              {invites.map((inv) => (
                <li key={inv.id} className="team-panel__row">
                  <div className="team-panel__row-main">
                    <div className="team-panel__user-name">{inv.email}</div>
                    <div className="team-panel__user-email">
                      Invited by {inv.invited_by_name || '—'} · expires {formatDate(inv.expires_at)}
                    </div>
                  </div>
                  <div className="team-panel__row-side">
                    <span className={`team-panel__role team-panel__role--${inv.role}`}>
                      {capitalize(inv.role)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function messageFromError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
